import { ReactNode } from 'react';
import BaseModal, {
	ModalBodyProps,
	ModalContainerProps,
	ModalFooterProps,
	ModalTitleProps,
} from 'src/components/primitives/BaseModal/BaseModal';
import BottomDrawer from 'src/components/Drawers/BaseDrawers/BottomDrawer/BottomDrawer';
import ResponsiveModalProvider, {
	useResponsiveModal,
} from 'src/components/Modals/ResponsiveModal/ResponsiveModalContext';

interface ResponsiveModalContainerWrapperProps extends ModalContainerProps {
	onClose: () => void;
}

function ResponsiveModalContainerWrapper({
	onClose,
	...rest
}: ResponsiveModalContainerWrapperProps) {
	return (
		<ResponsiveModalProvider onClose={onClose}>
			<ResponsiveModalContainer {...rest} />
		</ResponsiveModalProvider>
	);
}

type ResponsiveModalContainerProps = Omit<ModalContainerProps, 'onClose'>;

function ResponsiveModalContainer(props: ResponsiveModalContainerProps) {
	const { isMobileScreenSize, closeDrawer, isDrawerClosing, onClose } =
		useResponsiveModal();

	if (isMobileScreenSize) {
		return (
			<BottomDrawer.Container
				closeDrawer={closeDrawer}
				isDrawerClosing={isDrawerClosing}
				{...props}
			/>
		);
	}

	return <BaseModal.Container onClose={onClose} {...props} />;
}

type ResponseModalTitleProps<T extends string> = Omit<
	ModalTitleProps<T>,
	'onClose'
>;

function ResponseModalTitle<T extends string>(
	props: ResponseModalTitleProps<T>
) {
	const { isMobileScreenSize, closeDrawer, onClose } = useResponsiveModal();

	if (isMobileScreenSize) {
		return <BottomDrawer.Title closeDrawer={closeDrawer} {...props} />;
	}

	return (
		<BaseModal.Title onClose={onClose} {...(props as ModalTitleProps<T>)} />
	);
}

function ResponsiveModalBody(props: ModalBodyProps) {
	const { isMobileScreenSize } = useResponsiveModal();

	if (isMobileScreenSize) {
		return <BottomDrawer.Body {...props} />;
	}

	return <BaseModal.Body {...props} />;
}

interface ResponsiveModalFooterProps extends Omit<
	ModalFooterProps,
	'children'
> {
	/**
	 * Can either be a plain `ReactNode` or, if you need access to the responsive
	 * `close` function (typically needed when closing from a footer button), a
	 * render function that will receive `close` as an argument.
	 */
	children: ReactNode | ((props: { close: () => void }) => ReactNode);
}

function ResponsiveModalFooter({
	children,
	...rest
}: ResponsiveModalFooterProps) {
	const { isMobileScreenSize, close } = useResponsiveModal();

	const content =
		typeof children === 'function' ? children({ close }) : children;

	if (isMobileScreenSize) {
		return <BottomDrawer.Footer {...rest}>{content}</BottomDrawer.Footer>;
	}

	return <BaseModal.Footer {...rest}>{content}</BaseModal.Footer>;
}

export default {
	/**
	 * Renders its `children` either within a standard modal on screens > `sm`
	 * or a bottom drawer on screens <= `sm`.
	 *
	 * Useful for when the modal content is standard across screen sizes and only
	 * the wrapper (i.e. standard modal vs. bottom drawer) needs to change.
	 */
	Container: ResponsiveModalContainerWrapper,
	Title: ResponseModalTitle,
	Body: ResponsiveModalBody,
	Footer: ResponsiveModalFooter,
};
